import React, { useEffect, useRef, useState } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, updateDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../utils/firebase';
import {
  formatDistanceToNowStrict,
  parseISO,
  differenceInMinutes,
} from 'date-fns';
import notificationSound from '../assets/order-alert.mp3';
import { FaMobile, FaSoundcloud, FaSpeakerDeck, FaVolumeOff, FaVolumeUp, FaFileInvoiceDollar } from 'react-icons/fa';
import { MdOutlineVolumeMute, MdOutlineVolumeUp, MdSpeaker, MdVolumeMute } from 'react-icons/md';
import TodaysCompletedOrders from '../components/Orders/TodaysCompletedOrders';
import WeeklyBookings from '../components/Orders/WeeklyBookings';
import { useAuth } from "../state/AuthContext";

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const soundPlayedRef = useRef({});
  const audioRef = useRef(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const restaurantEmail = localStorage.getItem('user');
  const [, setRefresh] = useState(0); // dummy state to trigger rerender


  useEffect(() => {
    if (!restaurantEmail || typeof restaurantEmail !== 'string' || !restaurantEmail.includes('@')) {
      setErrorMsg('Invalid or missing restaurant email. Please log in again.');
      setLoading(false);
      return;
    }

    let unsubscribe;

    try {
      const q = query(
        collection(db, 'orders'),
        where('email', '==', restaurantEmail),
        where('delivered', '==', false),
        orderBy('createdDateTime', 'desc'),
        limit(50) // Only listen to latest 50 active orders
      );

      unsubscribe = onSnapshot(
        q,
        async (querySnapshot) => {
          const nowUTC = new Date();
          const activeOrders = [];
          const batch = writeBatch(db); // <-- initialize batch

          querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();

            if (!data.createdDateTime || !data.order) return;

            let created;
            try {
              created = parseISO(data.createdDateTime);
            } catch (err) {
              console.warn('Invalid timestamp format:', data.createdDateTime);
              return;
            }

            const minutesSince = differenceInMinutes(nowUTC, created);

            if (minutesSince >= 30) {
              // Auto-complete orders older than 30 minutes
              const orderRef = doc(db, 'orders', docSnapshot.id);
              batch.update(orderRef, { delivered: 'autoCompleted' });
            } else {
              // Active recent orders
              activeOrders.push({ id: docSnapshot.id, ...data });

              if (userInteracted && !soundPlayedRef.current[docSnapshot.id]) {
                soundPlayedRef.current[docSnapshot.id] = true;
                audioRef.current?.play();
              }
            }
          });

          // Commit batch if there are any updates
          if (!batch._mutations || batch._mutations.length > 0) {
            try {
              await batch.commit();
            } catch (error) {
              console.error('Failed to auto-complete old orders:', error);
            }
          }

          setOrders(
            activeOrders.sort(
              (a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime)
            )
          );
          setLoading(false);
        },
        (error) => {
          console.error('Firestore listen failed:', error);
          setErrorMsg('Failed to fetch orders. Please try again later.');
          setLoading(false);
        }
      );
    } catch (err) {
      console.error('Unexpected error setting up Firestore listener:', err);
      setErrorMsg('Unexpected error occurred.');
      setLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [restaurantEmail, userInteracted]);

  // Trigger rerender every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setRefresh((prev) => prev + 1);
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  const handleMarkDelivered = async (orderId) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        delivered: true,
      });
      setOrders((prevOrders) => prevOrders.filter((order) => order.id !== orderId));
    } catch (error) {
      console.error('Failed to mark order as delivered:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-gray-600">
        Loading orders...
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-red-500">
        {errorMsg}
      </div>
    );
  }

  const businessSubType = localStorage.getItem("businessSubType");
  const isServiceBusiness = businessSubType === "service";
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Sticky Header */}
      <header className="sticky top-0 z-[48] w-full bg-white/70 backdrop-blur-md border-b border-slate-100/80">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-5 group">

          {/* Live Status Brand */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#6cbb23] opacity-40"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#6cbb23] shadow-[0_0_10px_rgba(108,187,35,0.6)]"></span>
              </span>
            </div>

            <div className="flex flex-col">
              <h1 className="text-xl font-[1000] text-slate-900 tracking-tighter leading-none flex items-center gap-2">
                Your <span className="italic text-[#6cbb23]">Live</span> Orders
              </h1>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">
                Real-Time Terminal
              </span>
            </div>
          </div>

          {/* Modern Action Controls */}
          <div className="flex items-center gap-4">

            <a
              href="/taxreport"
              target="_blank"
              rel="noopener noreferrer"
              title="Download GST Report"
              className="
    relative overflow-hidden
    flex items-center justify-center
    w-11 h-11 rounded-full
    bg-slate-50 text-slate-400
    border border-slate-200/60
    hover:border-[#6cbb23] hover:text-[#6cbb23] hover:bg-white
    transition-all duration-500 ease-out active:scale-90
  "
            >
              <div className="relative z-10 scale-110">
                <FaFileInvoiceDollar />
              </div>

              {/* subtle hover ripple */}
              <div className="absolute inset-0 bg-slate-200/0 hover:bg-slate-100 transition-colors" />
            </a>

            {!userInteracted && (
              <button
                title="Enable System Audio"
                onClick={() => setUserInteracted(true)}
                className="
            relative overflow-hidden
            flex items-center justify-center
            w-11 h-11 rounded-full
            bg-slate-50 text-slate-400
            border border-slate-200/60
            hover:border-slate-300 hover:text-slate-600 hover:bg-white
            transition-all duration-500 ease-out active:scale-90
          "
              >
                <div className="relative z-10 scale-110">
                  <FaVolumeOff />
                </div>
                {/* Subtle hover ripple */}
                <div className="absolute inset-0 bg-slate-200/0 hover:bg-slate-100 transition-colors" />
              </button>
            )}

            {userInteracted && (
              <button
                title="Disable System Audio"
                onClick={() => setUserInteracted(false)}
                className="
            relative overflow-hidden
            flex items-center justify-center
            w-11 h-11 rounded-full
            bg-[#6cbb23] text-white
            shadow-[0_10px_20px_-5px_rgba(108,187,35,0.4)]
            hover:shadow-[0_15px_25px_-5px_rgba(108,187,35,0.5)]
            hover:scale-105
            transition-all duration-500 ease-out active:scale-95
          "
              >
                <div className="relative z-10 scale-110 animate-pulse">
                  <FaVolumeUp />
                </div>
                {/* Glass highlight effect */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent" />
              </button>
            )}
          </div>

        </div>
      </header>


      {/* Tabs Header */}
      <div className="mt-4">
        {/* Mobile View: Centered label */}
        <div className="flex justify-center lg:hidden">
          <button className="text-[#6cbb23] border-b-2 border-[#6cbb23] font-semibold pb-2">
            Live Incoming Orders
          </button>
        </div>

        {/* Desktop View: 60/40 split with centered buttons */}
        <div className="hidden lg:flex">
          <div className="w-[100%] flex justify-center items-center">
            <button className="text-[#6cbb23] border-b-2 border-[#6cbb23] font-semibold pb-2">
              Live Incoming Orders
            </button>
          </div>
          {/* <div className="w-[40%] flex justify-center items-center">
      <button className="text-gray-700 font-semibold pb-2">
        Today’s Orders
      </button>
    </div> */}
        </div>
      </div>



      {/* Orders List */}
      {/* Orders List */}

      <div className="flex flex-col lg:flex-row">
        {/* 70% Orders Section */}
        <main className="lg:w-[100%] px-4 py-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
            {orders.length === 0 ? (
              <div className="text-center text-gray-400 text-lg col-span-full">
                No recent orders.
              </div>
            ) : (
              orders.map((order) => {
                const created = parseISO(order.createdDateTime);
                const nowUTC = new Date(new Date().toISOString());
                const minutesLeft = 30 - differenceInMinutes(nowUTC, created);
                const percentRemaining = Math.max(0, (minutesLeft / 30) * 100);

                const parts = order.order.split('|');
                const items = parts[0]?.replace('Items:', '').trim();

                const preferredAtMatch = order.order.match(/Preferred At:\s*(.*?)\s*(\||$)/i);
                const preferredAt = preferredAtMatch?.[1]?.trim();

                const cookingInstructions = order.cookingInstructions;
                const customerPhone = order.customerPhone;
                const customerName = order.customerName;
                const tableNumber = order.tableNumber;
                const roomNumber = order.roomNumber;
                const orderType = order.orderType;
                const address = order.address;


                let orderHeader = '';

                if (orderType === "DELIVERY") {
                  orderHeader = '🚚 Delivery Order';
                }
                else if (orderType === "TAKEAWAY") {
                  orderHeader = '🥡 In-Store Order';
                }
                else if (orderType === "ROOM") {
                  orderHeader = `🛏️ Room #${roomNumber}`;
                }
                else if (orderType === "DINE_IN") {
                  orderHeader = `🍽️ Order from Table ${tableNumber}`;
                }
                else if (orderType === "BOOKING") {
                  orderHeader = `🥡 Appointment Booking`;
                }
                else {
                  orderHeader = '🛒 Order';
                }


                let progressBarColor = 'from-green-400 to-green-600';
                if (minutesLeft <= 20 && minutesLeft > 15) {
                  progressBarColor = 'from-orange-400 to-orange-600';
                } else if (minutesLeft <= 15) {
                  progressBarColor = 'from-red-400 to-red-600';
                }

                return (
                  <div
                    key={order.id}
                    className="relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-4 border border-gray-100 flex flex-col justify-between min-h-[260px]"
                  >
                    <div
                      className={`absolute top-0 left-0 h-1 bg-gradient-to-r ${progressBarColor} transition-all duration-500 ease-linear`}
                      style={{ width: `${percentRemaining}%` }}
                    ></div>

                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      {orderHeader}
                    </div>

                    <div className="space-y-2 text-gray-800 mb-4">
                      <div className="text-sm font-medium break-words">
                        {items}
                      </div>

                      <div className="text-xs font-semibold text-indigo-600">
                        🙋🏻‍♂️ {customerName}, {customerPhone}
                      </div>

                      {orderType === "DELIVERY" && address && (
                        <div className="text-xs text-gray-600 break-words">
                          📍 {address}
                        </div>
                      )}

                      {cookingInstructions && (
                        <div className="text-xs text-gray-500">
                          ✏️ {cookingInstructions}
                        </div>
                      )}
                      {preferredAt && (
                        <div className="text-xs text-blue-600 font-medium">
                          ⏰ Preferred At: {preferredAt}
                        </div>
                      )}
                      <div className="text-xs font-semibold text-indigo-600">
                        {orderType === "DINE_IN" && `Table: ${tableNumber}`}

                      </div>
                    </div>

                    <div className="mt-auto">
                      <div className="flex justify-between items-center text-xs text-gray-400 mb-3">
                        <span>
                          Placed {formatDistanceToNowStrict(created, { addSuffix: true })}
                        </span>
                        <span className="text-red-400">{minutesLeft} min left</span>
                      </div>

                      <button
                        className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-full text-xs font-semibold transition-colors"
                        onClick={() => handleMarkDelivered(order.id)}
                      >
                        Mark Delivered / Noted
                      </button>
                    </div>
                  </div>

                );
              })
            )}
          </div>
          {/* Show completed orders below cards on mobile */}

          <div className="lg:hidden mt-8">
            {isServiceBusiness ? <WeeklyBookings /> : <TodaysCompletedOrders />}
          </div>
        </main>

        {/* 30% Sidebar Section */}
        <aside className="hidden lg:block lg:w-[40%] px-4 py-8">
          {isServiceBusiness ? <WeeklyBookings /> : <TodaysCompletedOrders />}
        </aside>

      </div>


      <audio ref={audioRef} src={notificationSound} preload="auto" />
    </div>
  );
}

export default OrdersPage;
